/**
 * Import Data Screen — import workout history from Hevy, Strong, or CSV.
 * Accessible from Settings.
 */

import { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import * as DocumentPicker from 'expo-document-picker';
import {
  parseHevyCSV,
  parseStrongCSV,
  parseCSV,
  importWorkoutHistory,
  type ImportRow,
  type ImportResult,
} from '@/lib/dataImport';
import { TYPO } from '@/constants/typography';

type ImportSource = 'hevy' | 'strong' | 'generic';

interface PreviewData {
  source: ImportSource;
  rows: ImportRow[];
  workoutDays: number;
  mappedV1Count: number;
}

export default function ImportDataScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { user, profile } = useAuth();

  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [pickingSource, setPickingSource] = useState<ImportSource | null>(null);

  const uniqueDays = (rows: ImportRow[]) =>
    new Set(rows.map((r) => r.date.slice(0, 10))).size;

  const countMappedV1 = (rows: ImportRow[]) => {
    // rough count: exercises that matched DUEL core lifts
    const coreNames = ['Back Squat', 'Deadlift', 'Bench Press', 'Overhead Press', 'Weighted Pull-Up'];
    const seen = new Set<string>();
    for (const r of rows) {
      const n = r.exercise;
      if (coreNames.some((c) => n.toLowerCase().includes(c.toLowerCase().split(' ')[0].toLowerCase()))) {
        seen.add(n);
      }
    }
    return seen.size;
  };

  const handlePickFile = async (source: ImportSource) => {
    setPickingSource(source);
    setPreview(null);
    setImportResult(null);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'text/plain', 'application/octet-stream', '*/*'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        setPickingSource(null);
        return;
      }

      const asset = result.assets[0];
      if (!asset?.uri) {
        setPickingSource(null);
        return;
      }

      const resp = await fetch(asset.uri);
      const csvText = await resp.text();

      let rows: ImportRow[];
      if (source === 'hevy') {
        rows = parseHevyCSV(csvText);
      } else if (source === 'strong') {
        rows = parseStrongCSV(csvText);
      } else {
        rows = parseCSV(csvText);
      }

      if (rows.length === 0) {
        const msg = 'No workout data found. Make sure the file is a valid CSV export.';
        Platform.OS === 'web' ? alert(msg) : Alert.alert('Empty File', msg);
        setPickingSource(null);
        return;
      }

      setPreview({
        source,
        rows,
        workoutDays: uniqueDays(rows),
        mappedV1Count: countMappedV1(rows),
      });
    } catch (err: any) {
      const msg = err?.message || 'Failed to read file.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    } finally {
      setPickingSource(null);
    }
  };

  const handleImport = async () => {
    if (!preview || !user?.id) return;
    setImporting(true);

    try {
      const result = await importWorkoutHistory(
        user.id,
        preview.rows,
        profile?.bodyweight ?? 0,
        profile?.units ?? 'lbs',
      );
      setImportResult(result);
      setPreview(null);
    } catch (err: any) {
      const msg = err?.message || 'Import failed.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Import Error', msg);
    } finally {
      setImporting(false);
    }
  };

  const sourceLabel: Record<ImportSource, string> = {
    hevy: 'Hevy',
    strong: 'Strong',
    generic: 'CSV File',
  };

  return (
    <SafeAreaView style={{ backgroundColor: c.background, flex: 1 }} edges={['top']}>
      <View style={{ flex: 1 }}>
        {/* Header */}
        <LinearGradient
          colors={[c.background, c.surfaceVariant]}
          style={[styles.header, { borderBottomColor: c.border }]}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => router.back()} hitSlop={12} style={{ width: 28 }}>
              <ArrowLeft size={24} color={c.text} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: c.accent }]}>IMPORT DATA</Text>
            <View style={{ width: 28 }} />
          </View>
          <Text style={[styles.headerSubtitle, { color: c.textSecondary }]}>
            Bring your workout history from Hevy, Strong, or any CSV export.
            Imported workouts are mapped to DUEL lifts and added to your history.
          </Text>
        </LinearGradient>

        <ScrollView contentContainerStyle={styles.content}>
          {/* Source Buttons */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <Text style={[styles.sectionTitle, { color: c.text }]}>SELECT SOURCE</Text>

            {(
              [
                { source: 'hevy' as ImportSource, label: 'Import from Hevy', desc: 'Export from Hevy → Profile → Export Data' },
                { source: 'strong' as ImportSource, label: 'Import from Strong', desc: 'Export from Strong → Settings → Export Workouts' },
                { source: 'generic' as ImportSource, label: 'Import CSV File', desc: 'Generic CSV: date, exercise, weight, reps' },
              ] as const
            ).map(({ source, label, desc }) => (
              <TouchableOpacity
                key={source}
                style={[
                  styles.sourceButton,
                  {
                    backgroundColor: c.surfaceVariant,
                    borderColor: c.border,
                    opacity: pickingSource && pickingSource !== source ? 0.5 : 1,
                  },
                ]}
                disabled={!!pickingSource || importing}
                onPress={() => handlePickFile(source)}>
                <View style={styles.sourceButtonLeft}>
                  {pickingSource === source ? (
                    <ActivityIndicator size="small" color={c.accent} />
                  ) : (
                    <Upload size={20} color={c.accent} />
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.sourceButtonLabel, { color: c.text }]}>{label}</Text>
                    <Text style={[styles.sourceButtonDesc, { color: c.textSecondary }]}>{desc}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Preview */}
          {preview && (
            <View style={[styles.section, { backgroundColor: c.surface, borderColor: c.accent, borderWidth: 1 }]}>
              <View style={styles.sectionHeaderRow}>
                <FileText size={16} color={c.accent} />
                <Text style={[styles.sectionTitle, { color: c.accent }]}>PREVIEW</Text>
              </View>

              <Text style={[styles.previewText, { color: c.text }]}>
                Found{' '}
                <Text style={{ color: c.accent, fontWeight: '800' }}>{preview.rows.length}</Text>
                {' '}sets across{' '}
                <Text style={{ color: c.accent, fontWeight: '800' }}>{preview.workoutDays}</Text>
                {' '}days.{' '}
                <Text style={{ color: c.accent, fontWeight: '800' }}>{preview.mappedV1Count}</Text>
                {' '}exercises mapped to DUEL core lifts.
              </Text>
              <Text style={[styles.previewSource, { color: c.textTertiary }]}>
                Source: {sourceLabel[preview.source]}
              </Text>

              <TouchableOpacity
                style={[styles.importButton, { backgroundColor: c.accent }]}
                onPress={handleImport}
                disabled={importing}>
                {importing ? (
                  <ActivityIndicator color={c.accentForeground} />
                ) : (
                  <Text style={[styles.importButtonText, { color: c.accentForeground }]}>
                    Import {preview.rows.length} Sets
                  </Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.cancelButton, { borderColor: c.border }]}
                onPress={() => setPreview(null)}
                disabled={importing}>
                <Text style={[styles.cancelButtonText, { color: c.textSecondary }]}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Result */}
          {importResult && (
            <View style={[styles.section, { backgroundColor: c.surface }]}>
              <View style={styles.sectionHeaderRow}>
                {importResult.errors.length === 0 ? (
                  <CheckCircle size={16} color="#4ade80" />
                ) : (
                  <AlertCircle size={16} color="#facc15" />
                )}
                <Text style={[styles.sectionTitle, { color: c.text }]}>IMPORT COMPLETE</Text>
              </View>

              <Text style={[styles.resultText, { color: c.text }]}>
                Imported{' '}
                <Text style={{ color: c.accent, fontWeight: '800' }}>{importResult.imported}</Text>
                {' '}sets.{' '}
                <Text style={{ color: c.accent, fontWeight: '800' }}>
                  {importResult.v1LiftsFound.length}
                </Text>
                {' '}DUEL core lifts detected.
              </Text>

              {importResult.skipped > 0 && (
                <Text style={[styles.resultSubtext, { color: c.textTertiary }]}>
                  {importResult.skipped} sets skipped.
                </Text>
              )}

              {importResult.v1LiftsFound.length > 0 && (
                <Text style={[styles.resultSubtext, { color: c.textSecondary }]}>
                  Lifts: {importResult.v1LiftsFound.join(', ')}
                </Text>
              )}

              {importResult.errors.slice(0, 3).map((e, i) => (
                <Text key={i} style={[styles.errorText, { color: '#f87171' }]}>
                  ⚠ {e}
                </Text>
              ))}

              <TouchableOpacity
                style={[styles.importButton, { backgroundColor: c.surfaceVariant, marginTop: 12 }]}
                onPress={() => setImportResult(null)}>
                <Text style={[styles.importButtonText, { color: c.textSecondary }]}>Import Another File</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Tips */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <Text style={[styles.sectionTitle, { color: c.text }]}>HOW TO EXPORT</Text>
            {[
              { app: 'Hevy', steps: 'Open Hevy → Profile tab → Settings → Export Workout Data → CSV' },
              { app: 'Strong', steps: 'Open Strong → Settings → Export Workouts → Choose CSV' },
              { app: 'Generic CSV', steps: 'Format: date, exercise, weight, reps, sets (header row required)' },
            ].map(({ app, steps }) => (
              <View key={app} style={[styles.tipRow, { borderTopColor: c.border }]}>
                <Text style={[styles.tipApp, { color: c.accent }]}>{app}</Text>
                <Text style={[styles.tipSteps, { color: c.textSecondary }]}>{steps}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    gap: 8,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
  },
  headerSubtitle: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    lineHeight: 18,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
    gap: 16,
  },
  section: {
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 0.8,
  },
  sourceButton: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  sourceButtonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sourceButtonLabel: {
    fontSize: 15,
    fontFamily: TYPO.body.semibold,
    marginBottom: 2,
  },
  sourceButtonDesc: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  previewText: {
    fontSize: 15,
    fontFamily: TYPO.body.regular,
    lineHeight: 22,
  },
  previewSource: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  importButton: {
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
  },
  importButtonText: {
    fontSize: 15,
    fontFamily: TYPO.heading.bold,
  },
  cancelButton: {
    paddingVertical: 12,
    borderRadius: 2,
    alignItems: 'center',
    borderWidth: 1,
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
  },
  resultText: {
    fontSize: 15,
    fontFamily: TYPO.body.regular,
    lineHeight: 22,
  },
  resultSubtext: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
  },
  errorText: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  tipRow: {
    borderTopWidth: 1,
    paddingTop: 10,
    gap: 4,
  },
  tipApp: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
  tipSteps: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    lineHeight: 18,
  },
});
